/**
 * Backpressure for the moment filmstrip's thumbnail generation.
 *
 * Frames are requested by whichever cells are on screen, in whatever order they
 * mount, but they are produced by a single hidden player that seeks fastest when
 * it moves forward. So requests are held here, ordered by timestamp, and handed
 * over a few at a time.
 *
 * The queue can also be paused. Generation competes with the media the user is
 * actually watching — same decoder, same network — so while the video plays or a
 * trim handle is being dragged, dispatch stops and pending requests wait rather
 * than being dropped. This is the only correct place to pause: the cells enqueue
 * from a mount effect that does not re-run when playback stops, so gating there
 * would discard requests permanently instead of deferring them.
 *
 * `capture` is injected rather than reached for so this stays testable without a
 * browser.
 */

export type ThumbnailCapture = (timestamp: number, signal: AbortSignal) => Promise<string>;

type ThumbnailRequest = {
  frameIndex: number;
  timestamp: number;
  cacheKey: string;
  abortController: AbortController;
};

export type ThumbnailQueueOptions = {
  /**
   * Start paused. Matters when the queue is built while the video is already
   * playing — otherwise the first request dispatches before the caller's gate
   * effect has had a chance to run.
   */
  paused?: boolean;
};

export class ThumbnailQueue {
  private queue: ThumbnailRequest[] = [];
  private pending = new Map<string, ThumbnailRequest>();
  private isProcessing = false;
  private drainScheduled = false;
  private paused: boolean;
  private disposed = false;

  constructor(
    private readonly capture: ThumbnailCapture,
    private readonly onThumbnail: (cacheKey: string, thumbnail: string) => void,
    options: ThumbnailQueueOptions = {},
  ) {
    this.paused = options.paused ?? false;
  }

  addRequest(frameIndex: number, timestamp: number, cacheKey: string): AbortController {
    // A re-request for the same frame supersedes the old one.
    this.removeRequest(cacheKey);

    const abortController = new AbortController();
    const request: ThumbnailRequest = { frameIndex, timestamp, cacheKey, abortController };

    this.queue.push(request);
    this.pending.set(cacheKey, request);
    // Earliest timestamp first, so the player seeks forward through the strip.
    this.queue.sort((a, b) => a.timestamp - b.timestamp);

    this.scheduleDrain();

    return abortController;
  }

  removeRequest(cacheKey: string): void {
    const request = this.pending.get(cacheKey);
    if (!request) return;
    request.abortController.abort();
    this.queue = this.queue.filter((req) => req.cacheKey !== cacheKey);
    this.pending.delete(cacheKey);
  }

  /**
   * Stop or resume dispatch. Pausing lets the in-flight batch finish — those
   * grabs are already costing what they cost — and holds everything after it.
   * Resuming drains the same queued requests; nothing is discarded.
   */
  setPaused(paused: boolean): void {
    if (this.paused === paused) return;
    this.paused = paused;
    if (!paused) {
      this.scheduleDrain();
    }
  }

  get isPaused(): boolean {
    return this.paused;
  }

  /** Abort everything still waiting. Call on unmount. */
  dispose(): void {
    this.disposed = true;
    for (const request of this.queue) {
      request.abortController.abort();
    }
    this.queue = [];
    this.pending.clear();
  }

  get queueLength(): number {
    return this.queue.length;
  }

  /**
   * Start draining on a microtask rather than immediately.
   *
   * A screenful of cells enqueues in one commit, each from its own effect.
   * Dispatching synchronously would send the first-mounted frame off to seek
   * before its neighbours had even been queued, so the sort couldn't do its job
   * and the player's first move was as likely to be backwards as forwards.
   * Waiting a microtask lets the whole batch land and order itself first.
   */
  private scheduleDrain(): void {
    if (this.drainScheduled || this.isProcessing || this.paused || this.disposed) return;
    this.drainScheduled = true;
    void Promise.resolve().then(() => {
      this.drainScheduled = false;
      void this.processQueue();
    });
  }

  private async processQueue(): Promise<void> {
    if (this.isProcessing || this.paused || this.disposed || this.queue.length === 0) {
      return;
    }

    this.isProcessing = true;

    try {
      // One at a time, deliberately. There is no concurrency knob because there is
      // no concurrency to have: `capture` bottoms out in a single hidden player
      // driven by a strictly serial pump, so dispatching N at once only queues N
      // rows down there and the effective width stays 1. This used to hand over 3
      // and sleep 50ms between batches — the sleep was pure decoder idle time, and
      // the batch boundary both coarsened the pause check and blocked the next
      // request behind the slowest of the previous three.
      while (this.queue.length > 0) {
        // Re-checked every lap: playback can start mid-drain, and when it does
        // the rest of the queue should sit still until it stops.
        if (this.paused || this.disposed) break;

        const request = this.queue.shift();
        if (!request) break;
        await this.runRequest(request);
      }
    } finally {
      this.isProcessing = false;
    }
  }

  private async runRequest(request: ThumbnailRequest): Promise<void> {
    try {
      if (request.abortController.signal.aborted) return;

      const thumbnail = await this.capture(request.timestamp, request.abortController.signal);

      if (request.abortController.signal.aborted || this.disposed) return;
      this.onThumbnail(request.cacheKey, thumbnail);
    } catch (error) {
      if (!request.abortController.signal.aborted && !this.disposed) {
        console.warn(`Failed to generate thumbnail for frame ${request.frameIndex}:`, error);
      }
    } finally {
      // Identity-checked, not just key-checked. A request superseded while in
      // flight has already been replaced in `pending` under the same key, so
      // deleting by key alone would evict its *replacement* — leaving that one
      // untracked: `removeRequest` could no longer cancel it, a later
      // `addRequest` could no longer supersede it, and duplicates for one key
      // would pile up in the queue.
      if (this.pending.get(request.cacheKey) === request) {
        this.pending.delete(request.cacheKey);
      }
    }
  }
}
